import React, { useState, useEffect, useRef } from 'react';
import { auth } from '../../../../../firebase/config';
import Modal from '../../../../../components/Modal';
import TextField from '../../../../../components/TextField';
import Button from '../../../../../components/Button';
import { FiX, FiSearch, FiAlertTriangle, FiUpload, FiDownload } from 'react-icons/fi';
import Icon from '../../../../../components/Icon';
import { submitDeployment, replaceDeployment, fetchAvailableBranches, fetchAvailableCompanies, fetchDeployedCompaniesForBranch, processBulkDeployments } from '../service/addDeploymentService';
import type { Branch } from '../../../service/deploymentsService';
import type { Company } from '../../../../companies/service/companiesService';
import Toast from '../../../../../components/Toast';
import * as XLSX from 'xlsx';

type AddDeploymentModalProps = {
    isOpen: boolean;
    onClose: () => void;
    onSuccess: () => void;
};

function AddDeploymentModal({ isOpen, onClose, onSuccess }: AddDeploymentModalProps) {
    const [selectedBranch, setSelectedBranch] = useState<Branch | null>(null);
    const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
    const [selectedCompanies, setSelectedCompanies] = useState<{ [key: string]: Company | null }>({
        'CCTV': null,
        'Guard Company': null,
        'BAMS': null
    });
    const [availableBranches, setAvailableBranches] = useState<Branch[]>([]);
    const [availableCompanies, setAvailableCompanies] = useState<Company[]>([]);
    const [deployedCompanies, setDeployedCompanies] = useState<Company[]>([]);
    const [loadingDeployedCompanies, setLoadingDeployedCompanies] = useState(false);
    const [selectedCompanyType, setSelectedCompanyType] = useState<string | null>(null);
    const [showChangeWarningModal, setShowChangeWarningModal] = useState(false);
    const [pendingCompanyChange, setPendingCompanyChange] = useState<{ type: string; company: Company | null } | null>(null);
    
    // Modal states
    const [showBranchModal, setShowBranchModal] = useState(false);
    const [showCompanyModal, setShowCompanyModal] = useState(false);
    const [showConflictModal, setShowConflictModal] = useState(false);
    const [conflictData, setConflictData] = useState<{ existingDeployment: any; existingCompany: Company } | null>(null);
    
    // Search states
    const [branchSearchTerm, setBranchSearchTerm] = useState('');
    const [companySearchTerm, setCompanySearchTerm] = useState('');
    
    // UI states
    const [showToast, setShowToast] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    
    // Excel upload states
    const [isExcelMode, setIsExcelMode] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [showUploadModal, setShowUploadModal] = useState(false);
    const [uploadResults, setUploadResults] = useState<any[]>([]);
    const [undoStack, setUndoStack] = useState<any[]>([]); // Store previous deployments for undo
    const fileInputRef = useRef<HTMLInputElement>(null);

    const uid = auth.currentUser?.uid;
    const userRole = sessionStorage.getItem('role');
    const isBankUser = userRole === 'bank';

    // Fetch branches and companies when modal opens
    useEffect(() => {
        if (isOpen && uid) {
            const fetchData = async () => {
                setLoading(true);
                try {
                    const branches = await fetchAvailableBranches(uid, isBankUser);
                    setAvailableBranches(branches);
                    const companies = await fetchAvailableCompanies(uid, isBankUser);
                    setAvailableCompanies(companies);
                } catch (err) {
                    console.error('Error fetching data:', err);
                    showToastMessage('Failed to load data', 'error');
                } finally {
                    setLoading(false);
                }
            };
            fetchData();
        }
    }, [isOpen, uid, isBankUser]);

    // Reset form when modal closes
    useEffect(() => {
        if (!isOpen) {
            setSelectedBranch(null);
            setSelectedCompany(null);
            setBranchSearchTerm('');
            setCompanySearchTerm('');
            setShowBranchModal(false);
            setShowCompanyModal(false);
            setShowConflictModal(false);
            setConflictData(null);
            setIsExcelMode(false);
            setUploadResults([]);
            setShowUploadModal(false);
            setUndoStack([]);
            setDeployedCompanies([]);
            setSelectedCompanyType(null);
            setSelectedCompanies({
                'CCTV': null,
                'Guard Company': null,
                'BAMS': null
            });
            setShowChangeWarningModal(false);
            setPendingCompanyChange(null);
        }
    }, [isOpen]);

    const showToastMessage = (message: string, type: 'success' | 'error' | 'info' | 'warning' = 'success') => {
        setSuccessMessage(message);
        setShowToast(true);
        setTimeout(() => setShowToast(false), 3000);
    };

    // Filter branches
    const filteredBranches = availableBranches.filter(branch => {
        const address = branch.address || '';
        const branchCode = branch.branchCode || '';
        const searchTerm = branchSearchTerm.toLowerCase();
        return address.toLowerCase().includes(searchTerm) || branchCode.toLowerCase().includes(searchTerm);
    });

    // Filter companies
    const filteredCompanies = availableCompanies.filter(company => {
        const companyName = company.companyName || '';
        const companyId = company.companyId || '';
        const searchTerm = companySearchTerm.toLowerCase();
        return companyName.toLowerCase().includes(searchTerm) || companyId.toLowerCase().includes(searchTerm);
    });

    const handleSelectBranch = async (branch: Branch) => {
        setSelectedBranch(branch);
        setShowBranchModal(false);
        setBranchSearchTerm('');
        setDeployedCompanies([]);
        setSelectedCompanies({
            'CCTV': null,
            'Guard Company': null,
            'BAMS': null
        });
        
        // Fetch deployed companies for this branch
        if (uid) {
            setLoadingDeployedCompanies(true);
            try {
                const deployed = await fetchDeployedCompaniesForBranch(uid, isBankUser, branch.id);
                setDeployedCompanies(deployed);
                
                // Initialize selectedCompanies with deployed companies
                const initialSelected: { [key: string]: Company | null } = {
                    'CCTV': null,
                    'Guard Company': null,
                    'BAMS': null
                };
                deployed.forEach(company => {
                    if (company.companyType === 'CCTV') {
                        initialSelected['CCTV'] = company;
                    } else if (company.companyType === 'Guard Company') {
                        initialSelected['Guard Company'] = company;
                    } else if (company.companyType === 'BAMS') {
                        initialSelected['BAMS'] = company;
                    }
                });
                setSelectedCompanies(initialSelected);
            } catch (err) {
                console.error('Error fetching deployed companies:', err);
            } finally {
                setLoadingDeployedCompanies(false);
            }
        }
    };

    const handleSelectCompany = (company: Company) => {
        if (selectedCompanyType) {
            // Check if there's already a deployed company of this type
            const existingDeployed = deployedCompanies.find(c => c.companyType === selectedCompanyType);
            const existingSelected = selectedCompanies[selectedCompanyType];
            
            // If there's an existing deployment or selection, show warning
            if (existingDeployed || existingSelected) {
                setPendingCompanyChange({ type: selectedCompanyType, company });
                setShowChangeWarningModal(true);
            } else {
                // New deployment, no warning needed
                setSelectedCompanies(prev => ({
                    ...prev,
                    [selectedCompanyType]: company
                }));
                setShowCompanyModal(false);
                setCompanySearchTerm('');
                setSelectedCompanyType(null);
            }
        } else {
            // Fallback for old behavior
            setSelectedCompany(company);
            setShowCompanyModal(false);
            setCompanySearchTerm('');
        }
    };

    const handleConfirmCompanyChange = () => {
        if (pendingCompanyChange) {
            setSelectedCompanies(prev => ({
                ...prev,
                [pendingCompanyChange.type]: pendingCompanyChange.company
            }));
            setShowChangeWarningModal(false);
            setPendingCompanyChange(null);
            setShowCompanyModal(false);
            setCompanySearchTerm('');
            setSelectedCompanyType(null);
        }
    };

    const handleCancelCompanyChange = () => {
        setShowChangeWarningModal(false);
        setPendingCompanyChange(null);
    };

    const handleDeploy = async () => {
        if (!selectedBranch || !uid) {
            showToastMessage('Please select a branch', 'error');
            return;
        }

        // Get all selected companies
        const companiesToDeploy = Object.entries(selectedCompanies)
            .filter(([_, company]) => company !== null)
            .map(([type, company]) => ({ type, company: company! }));

        if (companiesToDeploy.length === 0) {
            showToastMessage('Please select at least one company to deploy', 'error');
            return;
        }

        setSaving(true);
        try {
            let successCount = 0;
            let errorCount = 0;
            let hasConflict = false;
            let conflictData: { existingDeployment: any; existingCompany: Company } | null = null;

            // Deploy each selected company
            for (const { type, company } of companiesToDeploy) {
                try {
                    // Check if this company type is already deployed
                    const existingDeployed = deployedCompanies.find(c => c.companyType === type);
                    
                    if (existingDeployed) {
                        // Company is being changed, need to find the deployment ID
                        // We'll need to fetch deployments to get the ID
                        const { fetchDeploymentBankId } = await import('../../../service/deploymentsService');
                        const { getDocs, collection } = await import('firebase/firestore');
                        const { db } = await import('../../../../../firebase/config');
                        const bankId = await fetchDeploymentBankId(uid, isBankUser);
                        
                        if (bankId) {
                            const deploymentsSnap = await getDocs(collection(db, "banks", bankId, "companyDeployment"));
                            const deployment = deploymentsSnap.docs
                                .map(doc => ({ id: doc.id, ...doc.data() }))
                                .find((d: any) => d.branchId === selectedBranch.id && d.companiesId === existingDeployed.id);
                            
                            if (deployment) {
                                // Replace existing deployment
                                await replaceDeployment(uid, isBankUser, selectedBranch.id, company.id, deployment.id);
                                successCount++;
                            } else {
                                // Deployment not found, try to create new one
                                const result = await submitDeployment(uid, isBankUser, selectedBranch.id, company.id);
                                if (!result.success && result.conflict) {
                                    hasConflict = true;
                                    conflictData = result.conflict;
                                    errorCount++;
                                } else {
                                    successCount++;
                                }
                            }
                        }
                    } else {
                        // New deployment
                        const result = await submitDeployment(uid, isBankUser, selectedBranch.id, company.id);
                        
                        if (!result.success && result.conflict) {
                            hasConflict = true;
                            conflictData = result.conflict;
                            errorCount++;
                        } else {
                            successCount++;
                        }
                    }
                } catch (err: any) {
                    console.error(`Error deploying ${type} company:`, err);
                    errorCount++;
                }
            }

            if (hasConflict && conflictData) {
                setConflictData(conflictData);
                setShowConflictModal(true);
            } else if (errorCount > 0) {
                showToastMessage(`⚠️ Deployed ${successCount} company(ies), ${errorCount} failed`, errorCount === companiesToDeploy.length ? 'error' : 'warning');
                if (successCount > 0) {
                    setTimeout(() => {
                        onSuccess();
                        onClose();
                    }, 2000);
                }
            } else {
                showToastMessage(`✅ Successfully deployed ${successCount} company(ies)!`, 'success');
                setTimeout(() => {
                    onSuccess();
                    onClose();
                }, 1000);
            }
        } catch (err: any) {
            console.error('Error deploying companies:', err);
            showToastMessage('❌ Failed to deploy companies', 'error');
        } finally {
            setSaving(false);
        }
    };

    const handleConflictResolve = async (shouldReplace: boolean) => {
        if (!selectedBranch || !uid || !conflictData) {
            return;
        }

        if (shouldReplace) {
            setSaving(true);
            try {
                // Find which company type this conflict is for
                const conflictCompanyType = conflictData.existingCompany?.companyType;
                const companyToDeploy = conflictCompanyType ? selectedCompanies[conflictCompanyType] : null;
                
                if (companyToDeploy) {
                    await replaceDeployment(uid, isBankUser, selectedBranch.id, companyToDeploy.id, conflictData.existingDeployment.id);
                    showToastMessage('✅ Company deployed successfully!', 'success');
                    setTimeout(() => {
                        onSuccess();
                        onClose();
                    }, 1000);
                } else {
                    showToastMessage('❌ Company not found for replacement', 'error');
                }
            } catch (err: any) {
                console.error('Error replacing deployment:', err);
                showToastMessage('❌ Failed to replace deployment', 'error');
            } finally {
                setSaving(false);
            }
        } else {
            setShowConflictModal(false);
            setConflictData(null);
        }
    };

    const downloadSampleFormat = () => {
        const sampleData = [
            { branchId: 'xxx', companyName: 'ESSPL' },
            { branchId: 'yyy', companyName: 'Company Name 2' },
        ];
        const workbook = XLSX.utils.book_new();
        const worksheet = XLSX.utils.json_to_sheet(sampleData);
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Sample');
        XLSX.writeFile(workbook, 'deployment_sample_format.xlsx');
    };

    const handleExcelUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file || !uid) return;

        setUploading(true);
        try {
            const data = await file.arrayBuffer();
            const workbook = XLSX.read(data, { type: 'array' });
            const sheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[sheetName];
            const jsonData = XLSX.utils.sheet_to_json(worksheet) as any[];

            const processResult = await processBulkDeployments(
                uid,
                isBankUser,
                jsonData,
                availableBranches,
                availableCompanies
            );
            const { results, undoData } = processResult;

            setUploadResults(results);
            setUndoStack(undoData); // Store undo data
            setShowUploadModal(true);

            const successCount = results.filter(r => r.status === 'success').length;
            const updatedCount = results.filter(r => r.status === 'updated').length;
            const errorCount = results.filter(r => r.status === 'error').length;
            const skipCount = results.filter(r => r.status === 'skip').length;

            if (successCount > 0 || updatedCount > 0) {
                showToastMessage(`✅ Bulk deployment completed: ${successCount} new, ${updatedCount} updated, ${errorCount} failed, ${skipCount} skipped`, 'success');
            } else {
                showToastMessage(`❌ Bulk deployment failed: ${errorCount} errors, ${skipCount} skipped`, 'error');
            }
        } catch (error: any) {
            console.error('Upload error:', error);
            showToastMessage('❌ Failed to process Excel file', 'error');
        } finally {
            setUploading(false);
            // Reset file input
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    };

    const handleModalClose = () => {
        // Auto-keep if there are updates and modal is closed
        if (undoStack.length > 0 && uploadResults.some(r => r.status === 'updated')) {
            // Already kept (no undo action taken)
            setUndoStack([]);
        }
        onClose();
    };

    const handleUndo = async () => {
        if (!uid || undoStack.length === 0) return;

        setUploading(true);
        try {
            const { updateDeploymentCompany } = await import('../../../service/deploymentsService');
            const { fetchDeploymentBankId } = await import('../../../service/deploymentsService');
            const bankId = await fetchDeploymentBankId(uid, isBankUser);

            if (!bankId) {
                showToastMessage('❌ Bank ID not found', 'error');
                return;
            }

            // Undo all updates
            for (const undoItem of undoStack) {
                if (undoItem.deploymentId && undoItem.previousCompanyId) {
                    await updateDeploymentCompany(bankId, undoItem.deploymentId, undoItem.previousCompanyId);
                }
            }

            // Update results to show undone
            setUploadResults(prev => prev.map(r => {
                if (r.status === 'updated') {
                    return { ...r, status: 'undone', message: `Undone: ${r.message}` };
                }
                return r;
            }));

            setUndoStack([]);
            showToastMessage('✅ Changes undone successfully', 'success');
            setTimeout(() => {
                onSuccess();
            }, 1000);
        } catch (error: any) {
            console.error('Undo error:', error);
            showToastMessage('❌ Failed to undo changes', 'error');
        } finally {
            setUploading(false);
        }
    };

    const handleKeep = () => {
        setUndoStack([]);
        setShowUploadModal(false);
        showToastMessage('✅ Changes kept', 'success');
        setTimeout(() => {
            onSuccess();
            onClose();
        }, 1000);
    };

    const downloadErrorReport = () => {
        const errorRows = uploadResults.filter(r => r.status === 'error');
        if (errorRows.length === 0) return;

        const workbook = XLSX.utils.book_new();
        const worksheet = XLSX.utils.json_to_sheet(errorRows.map(row => ({
            'Row Number': row.rowNumber,
            'Branch ID': row.branchId,
            'Company Name': row.companyName,
            'Error Message': row.message
        })));
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Errors');
        XLSX.writeFile(workbook, 'deployment_errors.xlsx');
    };

    const downloadFinalReport = () => {
        const workbook = XLSX.utils.book_new();
        const resultsData = uploadResults.map(row => ({
            'Row Number': row.rowNumber,
            'Branch ID': row.branchId,
            'Company Name': row.companyName,
            'Status': row.status,
            'Message': row.message
        }));
        const resultsWorksheet = XLSX.utils.json_to_sheet(resultsData);
        XLSX.utils.book_append_sheet(workbook, resultsWorksheet, 'Results');

        const summaryData = [
            { 'Metric': 'Total Rows', 'Count': uploadResults.length },
            { 'Metric': 'New Deployments', 'Count': uploadResults.filter(r => r.status === 'success').length },
            { 'Metric': 'Updated', 'Count': uploadResults.filter(r => r.status === 'updated').length },
            { 'Metric': 'Failed', 'Count': uploadResults.filter(r => r.status === 'error').length },
            { 'Metric': 'Skipped', 'Count': uploadResults.filter(r => r.status === 'skip').length },
        ];
        const summaryWorksheet = XLSX.utils.json_to_sheet(summaryData);
        XLSX.utils.book_append_sheet(workbook, summaryWorksheet, 'Summary');

        XLSX.writeFile(workbook, 'deployment_final_report.xlsx');
    };

    return (
        <>
            <Modal
                isOpen={isOpen}
                onClose={handleModalClose}
                title="Deploy Company to Branch"
                size="lg"
            >
                <div className="space-y-6 px-6">
                    {/* Mode Toggle */}
                    <div className="flex items-center justify-center gap-3 pb-4 border-b border-gray-200 dark:border-gray-700">
                        <span className={`text-sm font-medium ${!isExcelMode ? 'text-blue-600 dark:text-blue-400' : 'text-gray-500 dark:text-gray-400'}`}>
                            Single Deployment
                        </span>
                        <label className="relative inline-flex cursor-pointer items-center">
                            <input
                                type="checkbox"
                                checked={isExcelMode}
                                onChange={(e) => setIsExcelMode(e.target.checked)}
                                className="peer sr-only"
                            />
                            <div className="peer h-6 w-11 rounded-full bg-gray-200 transition-colors duration-200 after:absolute after:left-[2px] after:top-[2px] after:h-5 after:w-5 after:rounded-full after:border after:border-gray-300 after:bg-white after:transition-all after:content-[''] peer-checked:bg-blue-600 peer-checked:after:translate-x-full peer-checked:after:border-white peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:border-gray-600 dark:bg-gray-700 dark:peer-focus:ring-blue-800"></div>
                        </label>
                        <span className={`text-sm font-medium ${isExcelMode ? 'text-blue-600 dark:text-blue-400' : 'text-gray-500 dark:text-gray-400'}`}>
                            Excel Bulk Upload
                        </span>
                    </div>

                    {/* Single Deployment Mode */}
                    {!isExcelMode && (
                        <>
                            {/* Branch Selection */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Select Branch *
                                </label>
                                <div className="flex gap-2">
                                    <TextField
                                        type="text"
                                        placeholder={selectedBranch ? `${selectedBranch.branchCode} - ${selectedBranch.address}` : "Click to select branch"}
                                        value=""
                                        readOnly
                                        className="bg-gray-50 dark:bg-gray-800"
                                    />
                                    <Button
                                        variant="secondary"
                                        onClick={() => setShowBranchModal(true)}
                                    >
                                        Select
                                    </Button>
                                </div>
                            </div>

                            {/* Company Type Fields */}
                            {selectedBranch && (
                                <div className="space-y-4">
                                    {/* CCTV Company */}
                                    <div className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
                                        <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                                            CCTV Company
                                        </label>
                                        {loadingDeployedCompanies ? (
                                            <div className="text-sm text-gray-600 dark:text-gray-400">Loading...</div>
                                        ) : (() => {
                                            const selectedCctv = selectedCompanies['CCTV'];
                                            const deployedCctv = deployedCompanies.find(c => c.companyType === 'CCTV');
                                            const cctvCompany = selectedCctv || deployedCctv;
                                            return cctvCompany ? (
                                                <div className="space-y-2">
                                                    <div className="text-sm text-gray-900 dark:text-gray-100">
                                                        <strong>Company Name:</strong> {cctvCompany.companyName}
                                                    </div>
                                                    <div className="text-sm text-gray-600 dark:text-gray-400">
                                                        <strong>Company ID:</strong> {cctvCompany.companyId || 'N/A'}
                                                    </div>
                                                    {cctvCompany.contactEmail && (
                                                        <div className="text-sm text-gray-600 dark:text-gray-400">
                                                            <strong>Email:</strong> {cctvCompany.contactEmail}
                                                        </div>
                                                    )}
                                                    {cctvCompany.contactMobileNumber && (
                                                        <div className="text-sm text-gray-600 dark:text-gray-400">
                                                            <strong>Phone:</strong> {cctvCompany.contactMobileNumber}
                                                        </div>
                                                    )}
                                                    <div className="pt-2">
                                                        <Button
                                                            variant="secondary"
                                                            onClick={() => {
                                                                setSelectedCompanyType('CCTV');
                                                                setShowCompanyModal(true);
                                                            }}
                                                            className="text-sm"
                                                        >
                                                            Change
                                                        </Button>
                                                    </div>
                                                </div>
                                            ) : (
                                                <div className="flex gap-2">
                                                    <TextField
                                                        type="text"
                                                        placeholder={selectedCompanies['CCTV'] ? selectedCompanies['CCTV'].companyName : "Select Company"}
                                                        value={selectedCompanies['CCTV'] ? selectedCompanies['CCTV'].companyName : ""}
                                                        readOnly
                                                        className="bg-gray-50 dark:bg-gray-800 flex-1"
                                                    />
                                                    <Button
                                                        variant="secondary"
                                                        onClick={() => {
                                                            setSelectedCompanyType('CCTV');
                                                            setShowCompanyModal(true);
                                                        }}
                                                    >
                                                        {selectedCompanies['CCTV'] ? 'Change' : 'Select'}
                                                    </Button>
                                                </div>
                                            );
                                        })()}
                                    </div>

                                    {/* Guard Company */}
                                    <div className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
                                        <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                                            Guard Company
                                        </label>
                                        {loadingDeployedCompanies ? (
                                            <div className="text-sm text-gray-600 dark:text-gray-400">Loading...</div>
                                        ) : (() => {
                                            const selectedGuard = selectedCompanies['Guard Company'];
                                            const deployedGuard = deployedCompanies.find(c => c.companyType === 'Guard Company');
                                            const guardCompany = selectedGuard || deployedGuard;
                                            return guardCompany ? (
                                                <div className="space-y-2">
                                                    <div className="text-sm text-gray-900 dark:text-gray-100">
                                                        <strong>Company Name:</strong> {guardCompany.companyName}
                                                    </div>
                                                    <div className="text-sm text-gray-600 dark:text-gray-400">
                                                        <strong>Company ID:</strong> {guardCompany.companyId || 'N/A'}
                                                    </div>
                                                    {guardCompany.contactEmail && (
                                                        <div className="text-sm text-gray-600 dark:text-gray-400">
                                                            <strong>Email:</strong> {guardCompany.contactEmail}
                                                        </div>
                                                    )}
                                                    {guardCompany.contactMobileNumber && (
                                                        <div className="text-sm text-gray-600 dark:text-gray-400">
                                                            <strong>Phone:</strong> {guardCompany.contactMobileNumber}
                                                        </div>
                                                    )}
                                                    <div className="pt-2">
                                                        <Button
                                                            variant="secondary"
                                                            onClick={() => {
                                                                setSelectedCompanyType('Guard Company');
                                                                setShowCompanyModal(true);
                                                            }}
                                                            className="text-sm"
                                                        >
                                                            Change
                                                        </Button>
                                                    </div>
                                                </div>
                                            ) : (
                                                <div className="flex gap-2">
                                                    <TextField
                                                        type="text"
                                                        placeholder={selectedCompanies['Guard Company'] ? selectedCompanies['Guard Company'].companyName : "Select Company"}
                                                        value={selectedCompanies['Guard Company'] ? selectedCompanies['Guard Company'].companyName : ""}
                                                        readOnly
                                                        className="bg-gray-50 dark:bg-gray-800 flex-1"
                                                    />
                                                    <Button
                                                        variant="secondary"
                                                        onClick={() => {
                                                            setSelectedCompanyType('Guard Company');
                                                            setShowCompanyModal(true);
                                                        }}
                                                    >
                                                        {selectedCompanies['Guard Company'] ? 'Change' : 'Select'}
                                                    </Button>
                                                </div>
                                            );
                                        })()}
                                    </div>

                                    {/* BAMS Company */}
                                    <div className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
                                        <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                                            BAMS Company
                                        </label>
                                        {loadingDeployedCompanies ? (
                                            <div className="text-sm text-gray-600 dark:text-gray-400">Loading...</div>
                                        ) : (() => {
                                            const selectedBams = selectedCompanies['BAMS'];
                                            const deployedBams = deployedCompanies.find(c => c.companyType === 'BAMS');
                                            const bamsCompany = selectedBams || deployedBams;
                                            return bamsCompany ? (
                                                <div className="space-y-2">
                                                    <div className="text-sm text-gray-900 dark:text-gray-100">
                                                        <strong>Company Name:</strong> {bamsCompany.companyName}
                                                    </div>
                                                    <div className="text-sm text-gray-600 dark:text-gray-400">
                                                        <strong>Company ID:</strong> {bamsCompany.companyId || 'N/A'}
                                                    </div>
                                                    {bamsCompany.contactEmail && (
                                                        <div className="text-sm text-gray-600 dark:text-gray-400">
                                                            <strong>Email:</strong> {bamsCompany.contactEmail}
                                                        </div>
                                                    )}
                                                    {bamsCompany.contactMobileNumber && (
                                                        <div className="text-sm text-gray-600 dark:text-gray-400">
                                                            <strong>Phone:</strong> {bamsCompany.contactMobileNumber}
                                                        </div>
                                                    )}
                                                    <div className="pt-2">
                                                        <Button
                                                            variant="secondary"
                                                            onClick={() => {
                                                                setSelectedCompanyType('BAMS');
                                                                setShowCompanyModal(true);
                                                            }}
                                                            className="text-sm"
                                                        >
                                                            Change
                                                        </Button>
                                                    </div>
                                                </div>
                                            ) : (
                                                <div className="flex gap-2">
                                                    <TextField
                                                        type="text"
                                                        placeholder={selectedCompanies['BAMS'] ? selectedCompanies['BAMS'].companyName : "Select Company"}
                                                        value={selectedCompanies['BAMS'] ? selectedCompanies['BAMS'].companyName : ""}
                                                        readOnly
                                                        className="bg-gray-50 dark:bg-gray-800 flex-1"
                                                    />
                                                    <Button
                                                        variant="secondary"
                                                        onClick={() => {
                                                            setSelectedCompanyType('BAMS');
                                                            setShowCompanyModal(true);
                                                        }}
                                                    >
                                                        {selectedCompanies['BAMS'] ? 'Change' : 'Select'}
                                                    </Button>
                                                </div>
                                            );
                                        })()}
                                    </div>
                                </div>
                            )}

                            {/* Actions */}
                            <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                                <Button variant="secondary" onClick={onClose}>
                                    Cancel
                                </Button>
                                <Button
                                    onClick={handleDeploy}
                                    disabled={!selectedBranch || saving || Object.values(selectedCompanies).every(c => c === null)}
                                >
                                    {saving ? 'Deploying...' : 'Deploy Companies'}
                                </Button>
                            </div>
                        </>
                    )}

                    {/* Excel Upload Mode */}
                    {isExcelMode && (
                        <div className="space-y-4">
                            <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 dark:border-blue-800 dark:bg-blue-900/20">
                                <p className="text-sm text-blue-800 dark:text-blue-200">
                                    <strong>Excel Format:</strong> The Excel file should contain columns: <code className="bg-blue-100 dark:bg-blue-900 px-1 rounded">branchId</code> and <code className="bg-blue-100 dark:bg-blue-900 px-1 rounded">companyName</code>
                                </p>
                            </div>
                            
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Upload Excel File
                                </label>
                                <div className="flex gap-2">
                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        accept=".xlsx,.xls"
                                        onChange={handleExcelUpload}
                                        className="hidden"
                                    />
                                    <Button
                                        variant="secondary"
                                        onClick={() => fileInputRef.current?.click()}
                                        disabled={uploading}
                                        className="flex items-center gap-2"
                                    >
                                        <Icon icon={FiUpload} className="h-4 w-4" />
                                        {uploading ? 'Uploading...' : 'Choose File'}
                                    </Button>
                                    <Button
                                        variant="secondary"
                                        onClick={downloadSampleFormat}
                                        className="flex items-center gap-2"
                                    >
                                        <Icon icon={FiDownload} className="h-4 w-4" />
                                        Download Sample
                                    </Button>
                                </div>
                            </div>

                            <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                                <Button variant="secondary" onClick={onClose}>
                                    Cancel
                                </Button>
                            </div>
                        </div>
                    )}
                </div>
            </Modal>

            {/* Branch Selection Modal */}
            <Modal
                isOpen={showBranchModal}
                onClose={() => setShowBranchModal(false)}
                title="Select Branch"
                size="xl"
            >
                <div className="space-y-4 px-6">
                    <div className="relative">
                        <Icon icon={FiSearch} className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
                        <input
                            type="text"
                            placeholder="Search by address or branch code..."
                            value={branchSearchTerm}
                            onChange={(e) => setBranchSearchTerm(e.target.value)}
                            className="w-full rounded-xl border border-gray-300 bg-white py-2.5 pl-10 pr-4 text-sm outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:border-gray-600 dark:bg-black/30 dark:text-gray-100"
                        />
                    </div>
                    <div className="max-h-96 overflow-y-auto">
                        <table className="w-full border-collapse">
                            <thead>
                                <tr className="border-b-2 border-gray-300 bg-gray-50 dark:bg-gray-800">
                                    <th className="border-r border-gray-200 px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-gray-700 dark:border-gray-600 dark:text-gray-300">
                                        Branch Code
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-gray-700 dark:text-gray-300">
                                        Address
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-gray-700 dark:text-gray-300">
                                        Action
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredBranches.length === 0 ? (
                                    <tr>
                                        <td colSpan={3} className="px-4 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                                            No branches found
                                        </td>
                                    </tr>
                                ) : (
                                    filteredBranches.map((branch) => (
                                        <tr key={branch.id} className="border-b border-gray-200 hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-gray-800/50">
                                            <td className="border-r border-gray-200 px-4 py-3 dark:border-gray-700">
                                                <span className="inline-flex items-center rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-medium text-blue-800 dark:bg-blue-900/30 dark:text-blue-300">
                                                    {branch.branchCode}
                                                </span>
                                            </td>
                                            <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400" style={{ wordWrap: 'break-word', whiteSpace: 'normal' }}>
                                                {branch.address}
                                            </td>
                                            <td className="px-4 py-3">
                                                <Button
                                                    onClick={() => handleSelectBranch(branch)}
                                                    className="px-3 py-1.5 text-xs"
                                                >
                                                    Select
                                                </Button>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </Modal>

            {/* Company Selection Modal */}
            <Modal
                isOpen={showCompanyModal}
                onClose={() => setShowCompanyModal(false)}
                title="Select Company"
                size="xl"
            >
                <div className="space-y-4 px-6">
                    <div className="relative">
                        <Icon icon={FiSearch} className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
                        <input
                            type="text"
                            placeholder="Search by company name or ID..."
                            value={companySearchTerm}
                            onChange={(e) => setCompanySearchTerm(e.target.value)}
                            className="w-full rounded-xl border border-gray-300 bg-white py-2.5 pl-10 pr-4 text-sm outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:border-gray-600 dark:bg-black/30 dark:text-gray-100"
                        />
                    </div>
                    <div className="max-h-96 overflow-y-auto">
                        <table className="w-full border-collapse">
                            <thead>
                                <tr className="border-b-2 border-gray-300 bg-gray-50 dark:bg-gray-800">
                                    <th className="border-r border-gray-200 px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-gray-700 dark:border-gray-600 dark:text-gray-300">
                                        Company Name
                                    </th>
                                    <th className="border-r border-gray-200 px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-gray-700 dark:border-gray-600 dark:text-gray-300">
                                        Type
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-gray-700 dark:text-gray-300">
                                        Company ID
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-gray-700 dark:text-gray-300">
                                        Action
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredCompanies.length === 0 ? (
                                    <tr>
                                        <td colSpan={4} className="px-4 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                                            No companies found
                                        </td>
                                    </tr>
                                ) : (
                                    filteredCompanies.map((company) => (
                                        <tr key={company.id} className="border-b border-gray-200 hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-gray-800/50">
                                            <td className="border-r border-gray-200 px-4 py-3 text-sm font-medium text-gray-900 dark:text-gray-100" style={{ wordWrap: 'break-word', whiteSpace: 'normal' }}>
                                                <strong>{company.companyName}</strong>
                                            </td>
                                            <td className="border-r border-gray-200 px-4 py-3 dark:border-gray-700">
                                                <span className="inline-flex items-center rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-medium text-blue-800 dark:bg-blue-900/30 dark:text-blue-300">
                                                    {company.companyType || 'N/A'}
                                                </span>
                                            </td>
                                            <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                                                {company.companyId || 'N/A'}
                                            </td>
                                            <td className="px-4 py-3">
                                                <Button
                                                    onClick={() => handleSelectCompany(company)}
                                                    className="px-3 py-1.5 text-xs"
                                                >
                                                    Select
                                                </Button>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </Modal>

            {/* Change Company Warning Modal */}
            <Modal
                isOpen={showChangeWarningModal}
                onClose={handleCancelCompanyChange}
                title="Change Company Warning"
                size="md"
            >
                <div className="space-y-4 px-6">
                    <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-4 dark:border-yellow-800 dark:bg-yellow-900/20">
                        <div className="flex items-center gap-2">
                            <Icon icon={FiAlertTriangle} className="h-5 w-5 text-yellow-600 dark:text-yellow-400" />
                            <strong className="text-yellow-900 dark:text-yellow-100">Warning:</strong>
                        </div>
                        <p className="mt-2 text-sm text-yellow-800 dark:text-yellow-200">
                            You are about to change the {pendingCompanyChange?.type} company. This will replace the existing deployment.
                        </p>
                    </div>

                    {pendingCompanyChange && (
                        <>
                            <div>
                                <strong className="text-gray-900 dark:text-gray-100">Current Company:</strong>
                                <div className="mt-1 ml-3 text-sm text-gray-600 dark:text-gray-400">
                                    {(() => {
                                        const current = deployedCompanies.find(c => c.companyType === pendingCompanyChange.type) || 
                                                      selectedCompanies[pendingCompanyChange.type];
                                        return current ? `${current.companyName} (${current.companyId || 'N/A'})` : 'None';
                                    })()}
                                </div>
                            </div>

                            <div>
                                <strong className="text-gray-900 dark:text-gray-100">New Company:</strong>
                                <div className="mt-1 ml-3 text-sm text-gray-600 dark:text-gray-400">
                                    {pendingCompanyChange.company ? 
                                        `${pendingCompanyChange.company.companyName} (${pendingCompanyChange.company.companyId || 'N/A'})` : 
                                        'None'}
                                </div>
                            </div>
                        </>
                    )}

                    <p className="text-sm text-gray-600 dark:text-gray-400">
                        Are you sure you want to proceed with this change?
                    </p>

                    <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                        <Button variant="secondary" onClick={handleCancelCompanyChange}>
                            Cancel
                        </Button>
                        <Button 
                            variant="primary" 
                            onClick={handleConfirmCompanyChange}
                            className="bg-yellow-600 hover:bg-yellow-700 focus:ring-yellow-500"
                        >
                            Confirm Change
                        </Button>
                    </div>
                </div>
            </Modal>

            {/* Conflict Resolution Modal */}
            <Modal
                isOpen={showConflictModal}
                onClose={() => handleConflictResolve(false)}
                title="Deployment Conflict"
                size="md"
            >
                <div className="space-y-4 px-6">
                    <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-4 dark:border-yellow-800 dark:bg-yellow-900/20">
                        <div className="flex items-center gap-2">
                            <Icon icon={FiAlertTriangle} className="h-5 w-5 text-yellow-600 dark:text-yellow-400" />
                            <strong className="text-yellow-900 dark:text-yellow-100">Warning:</strong>
                        </div>
                        <p className="mt-2 text-sm text-yellow-800 dark:text-yellow-200">
                            Branch already has a company of the same type deployed.
                        </p>
                    </div>

                    <div>
                        <strong className="text-gray-900 dark:text-gray-100">Current Company:</strong>
                        <div className="mt-1 ml-3 text-sm text-gray-600 dark:text-gray-400">
                            <strong>Company:</strong> {conflictData?.existingCompany?.companyName} ({conflictData?.existingCompany?.companyType})
                        </div>
                    </div>

                    <div>
                        <strong className="text-gray-900 dark:text-gray-100">New Company:</strong>
                        <div className="mt-1 ml-3 text-sm text-gray-600 dark:text-gray-400">
                            {(() => {
                                const newCompanyType = conflictData?.existingCompany?.companyType;
                                const newCompany = newCompanyType ? selectedCompanies[newCompanyType] : null;
                                return newCompany ? (
                                    <>
                                        <strong>Company:</strong> {newCompany.companyName} ({newCompany.companyType})
                                    </>
                                ) : (
                                    <span>No company selected</span>
                                );
                            })()}
                        </div>
                    </div>

                    <p className="text-sm text-gray-600 dark:text-gray-400">
                        If you proceed, the existing company will be replaced.
                    </p>

                    <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                        <Button variant="secondary" onClick={() => handleConflictResolve(false)}>
                            Cancel
                        </Button>
                        <Button 
                            variant="primary" 
                            onClick={() => handleConflictResolve(true)} 
                            disabled={saving}
                            className="bg-yellow-600 hover:bg-yellow-700 focus:ring-yellow-500"
                        >
                            {saving ? 'Replacing...' : 'Replace Existing Company'}
                        </Button>
                    </div>
                </div>
            </Modal>

            {/* Upload Results Modal */}
            <Modal
                isOpen={showUploadModal}
                onClose={handleModalClose}
                title="Bulk Upload Results"
                size="xl"
            >
                <div className="space-y-4 px-6">
                    <div className="flex justify-between items-center">
                        <div className="flex gap-2 flex-wrap">
                            <span className="inline-flex items-center rounded-full bg-green-100 px-3 py-1 text-xs font-medium text-green-800 dark:bg-green-900/30 dark:text-green-300">
                                {uploadResults.filter(r => r.status === 'success').length} New
                            </span>
                            <span className="inline-flex items-center rounded-full bg-blue-100 px-3 py-1 text-xs font-medium text-blue-800 dark:bg-blue-900/30 dark:text-blue-300">
                                {uploadResults.filter(r => r.status === 'updated').length} Updated
                            </span>
                            <span className="inline-flex items-center rounded-full bg-yellow-100 px-3 py-1 text-xs font-medium text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300">
                                {uploadResults.filter(r => r.status === 'skip').length} Skipped
                            </span>
                            <span className="inline-flex items-center rounded-full bg-red-100 px-3 py-1 text-xs font-medium text-red-800 dark:bg-red-900/30 dark:text-red-300">
                                {uploadResults.filter(r => r.status === 'error').length} Failed
                            </span>
                        </div>
                        <div className="flex gap-2">
                            <Button
                                variant="secondary"
                                onClick={downloadFinalReport}
                                className="flex items-center gap-2 text-xs"
                            >
                                <Icon icon={FiDownload} className="h-4 w-4" />
                                Download Report
                            </Button>
                            {uploadResults.filter(r => r.status === 'error').length > 0 && (
                                <Button
                                    variant="secondary"
                                    onClick={downloadErrorReport}
                                    className="flex items-center gap-2 text-xs bg-red-50 hover:bg-red-100 text-red-700 dark:bg-red-900/20 dark:text-red-300"
                                >
                                    <Icon icon={FiDownload} className="h-4 w-4" />
                                    Download Errors
                                </Button>
                            )}
                        </div>
                    </div>
                    <div className="max-h-96 overflow-y-auto">
                        <table className="w-full border-collapse">
                            <thead>
                                <tr className="border-b-2 border-gray-300 bg-gray-50 dark:bg-gray-800">
                                    <th className="border-r border-gray-200 px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-gray-700 dark:border-gray-600 dark:text-gray-300">
                                        Row
                                    </th>
                                    <th className="border-r border-gray-200 px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-gray-700 dark:border-gray-600 dark:text-gray-300">
                                        Branch ID
                                    </th>
                                    <th className="border-r border-gray-200 px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-gray-700 dark:border-gray-600 dark:text-gray-300">
                                        Company Name
                                    </th>
                                    <th className="border-r border-gray-200 px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-gray-700 dark:border-gray-600 dark:text-gray-300">
                                        Status
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-gray-700 dark:text-gray-300">
                                        Message
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {uploadResults.map((result, index) => (
                                    <tr
                                        key={index}
                                        className={`border-b border-gray-200 dark:border-gray-700 ${
                                            result.status === 'error' ? 'bg-red-50 dark:bg-red-900/10' :
                                            result.status === 'skip' ? 'bg-yellow-50 dark:bg-yellow-900/10' :
                                            result.status === 'updated' ? 'bg-blue-50 dark:bg-blue-900/10' :
                                            result.status === 'undone' ? 'bg-gray-50 dark:bg-gray-900/10' :
                                            'bg-green-50 dark:bg-green-900/10'
                                        }`}
                                    >
                                        <td className="border-r border-gray-200 px-4 py-3 text-sm dark:border-gray-700">
                                            {result.rowNumber || index + 1}
                                        </td>
                                        <td className="border-r border-gray-200 px-4 py-3 text-sm dark:border-gray-700">
                                            {result.branchId}
                                        </td>
                                        <td className="border-r border-gray-200 px-4 py-3 text-sm dark:border-gray-700">
                                            {result.companyName}
                                        </td>
                                        <td className="border-r border-gray-200 px-4 py-3 dark:border-gray-700">
                                            <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                                                result.status === 'success' ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300' :
                                                result.status === 'updated' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300' :
                                                result.status === 'undone' ? 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-300' :
                                                result.status === 'skip' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300' :
                                                'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
                                            }`}>
                                                {result.status}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3 text-sm" style={{ wordWrap: 'break-word', whiteSpace: 'normal' }}>
                                            {result.message}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                        {undoStack.length > 0 && uploadResults.some(r => r.status === 'updated') && (
                            <Button 
                                variant="secondary" 
                                onClick={handleUndo}
                                disabled={uploading}
                                className="bg-orange-50 hover:bg-orange-100 text-orange-700 dark:bg-orange-900/20 dark:text-orange-300"
                            >
                                Undo Updates
                            </Button>
                        )}
                        {undoStack.length > 0 && uploadResults.some(r => r.status === 'updated') && (
                            <Button 
                                variant="primary" 
                                onClick={handleKeep}
                            >
                                Keep Changes
                            </Button>
                        )}
                        {(!undoStack.length || !uploadResults.some(r => r.status === 'updated')) && (
                            <Button variant="secondary" onClick={handleModalClose}>
                                Close
                            </Button>
                        )}
                    </div>
                </div>
            </Modal>

            {/* Toast */}
            {showToast && (
                <div className="pointer-events-none fixed right-4 top-4 z-50">
                    <div className="pointer-events-auto">
                        <Toast
                            message={successMessage}
                            type={successMessage.includes('❌') ? 'error' : 'success'}
                            onClose={() => setShowToast(false)}
                        />
                    </div>
                </div>
            )}
        </>
    );
}

export default AddDeploymentModal;

